/**
 * PWA Install Prompt Component
 *
 * PURPOSE: Encourage mobile users to install app to home screen
 * ISOLATION: Standalone component - does NOT modify existing UI
 * ROLLBACK: Hidden when VITE_ENABLE_PWA_FEATURES=false
 *
 * USAGE: Add to App.tsx: <PWAInstallPrompt />
 * BEHAVIOR:
 * - Auto-shows on mobile after 3 visits (localStorage tracking)
 * - Dismissible by user
 * - iOS: Shows manual instructions (Safari doesn't support beforeinstallprompt)
 * - Android/Chrome: Uses native install prompt API
 *
 * PRESERVATION GUARANTEE:
 * - Does NOT touch MobileImpactDashboard
 * - Does NOT touch useMediaQuery
 * - Does NOT touch existing responsive layouts
 * - Pure overlay component with no layout impact
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import FEATURE_FLAGS from '../../config/featureFlags';

const PromptContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: var(--spacing-lg);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.2);
  z-index: 9999; /* Above all existing UI */
  max-width: 100%;
`;

const PromptContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h3`
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.p`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 14px;
  opacity: 0.9;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
`;

const InstallButton = styled.button`
  min-height: 44px;
  min-width: 120px;
  background: white;
  color: #667eea;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    transform: scale(0.95);
  }
`;

const DismissButton = styled.button`
  min-height: 44px;
  min-width: 120px;
  background: transparent;
  color: white;
  border: 1px solid white;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;

  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
`;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAInstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // FEATURE FLAG CHECK - Exit early if PWA features disabled
    if (!FEATURE_FLAGS.ENABLE_PWA_INSTALL_PROMPT) {
      console.log('ℹ️  PWA install prompt disabled by feature flag');
      return;
    }

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone;
    if (isStandalone) {
      console.log('ℹ️  PWA already installed');
      return;
    }

    // Check if user already dismissed
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed === 'true') {
      console.log('ℹ️  PWA install prompt previously dismissed');
      return;
    }

    // Detect iOS (different install flow)
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Only show on mobile devices
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      console.log('ℹ️  PWA install prompt disabled on desktop');
      return;
    }

    // Track visits (show after 3 visits)
    const visits = parseInt(localStorage.getItem('pwa_visits') || '0');
    localStorage.setItem('pwa_visits', (visits + 1).toString());

    if (visits >= 2) {
      // Show prompt after short delay (let page load first)
      setTimeout(() => setShowPrompt(true), 3000);
    }

    // Listen for beforeinstallprompt (Chrome/Edge/Samsung Internet)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      console.log('ℹ️  beforeinstallprompt event captured');
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('⚠️  No deferred prompt available');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`ℹ️  User choice: ${outcome}`);

      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.setItem('pwa_install_dismissed', 'true');
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error('❌ Install prompt error:', error);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_install_dismissed', 'true');
    setShowPrompt(false);
  };

  // Don't render if feature disabled or prompt not ready
  if (!FEATURE_FLAGS.ENABLE_PWA_INSTALL_PROMPT || !showPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <PromptContainer
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <PromptContent>
            <Title>📱 Install ConnectPath</Title>

            {isIOS ? (
              <Description>
                Tap the <strong>Share</strong> button <span style={{ fontSize: '20px' }}>⎙</span> at the bottom of your screen,
                then select <strong>"Add to Home Screen"</strong> to install the app.
              </Description>
            ) : (
              <Description>
                Add ConnectPath to your home screen for quick access, offline support, and a native app experience.
              </Description>
            )}

            <ButtonGroup>
              {!isIOS && deferredPrompt && (
                <InstallButton onClick={handleInstall}>
                  Install Now
                </InstallButton>
              )}
              <DismissButton onClick={handleDismiss}>
                {isIOS ? 'Got It' : 'Maybe Later'}
              </DismissButton>
            </ButtonGroup>
          </PromptContent>
        </PromptContainer>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
